import React, { useCallback, useEffect, useRef, useState } from 'react'
import AceEditor from 'react-ace'
import ReactAce from 'react-ace'
import 'ace-builds/src-noconflict/mode-sparql'
import 'ace-builds/src-noconflict/mode-json'
import 'ace-builds/src-noconflict/mode-javascript'
import 'ace-builds/src-noconflict/mode-turtle'
import 'ace-builds/src-noconflict/mode-html'
import './turtle-completer'
import { AceThemeName, getLoadedAceTheme, LoadedAceTheme, loadAceTheme } from './ace-themes'
import { MiniButton } from '@/components/ui-primitives/small'
import { mergeClasses } from '@/helpers/styling'
import { Braces, Check, Clipboard, Copy, Download, TriangleAlert, Upload } from 'lucide-react'
import { OpenInNewWindowIcon } from '@radix-ui/react-icons'
import { useLocalSettings } from '../local-settings'
import { Panel, usePanelRegistry } from '../panels/panel-registry'
import { useExperimentalConfiguratorUx } from '../experimental-ux'
import { TopBarButton, TopBarSeparator } from '@/components/editor/configuration-top-bar/primitives'
import { SparqlEditor } from '@/components/SPARQLEditor'
import { store } from '@/store'
import { CodeEditorHandle } from '@/redux/current-viewer'
import { useLatest } from "@/hooks/use-latest"
import { getModeAbilities } from './mode-abilities'
import { HistoryMenu } from './history-menu'

const DEFAULT_ACE_THEME: AceThemeName = 'textmate'

export type CodeEditorMode = 'json' | 'sparql' | 'javascript' | 'turtle' | 'html'

export type CodeEditorProps = {
  name?: string
  mode: CodeEditorMode
  extension?: string
  showGutter?: boolean
  exportFileName?: string
  value: string
  onValueChange?: (str: string) => void
  readonly?: boolean
  publishValueChangeOnBlur?: boolean
  floating?: boolean // This is different from panel docking/float context, as we can pop-out al already floating editor
  fullHeight?: boolean
  minHeight?: string
  autoFocus?: boolean
  onRun?: () => void // bound to Cmd/Ctrl+Enter
  /** Keeps earlier versions of this field in localStorage under this key, with a menu to restore one */
  localHistoryKey?: string
}

export function CodeEditor(props: CodeEditorProps) {
  const editorRef = useRef<ReactAce>(null)
  const focusedRef = useRef<boolean>(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const [ spawnFloating, setSpawnFloating ] = useState(false)
  const isFloating = props.floating
  const [ validationStatus, setValidationStatus ] = useState<string | "ok" | null>(null)
  const localSettings = useLocalSettings()
  const loadedAceTheme = useLoadedAceTheme(localSettings.aceTheme ?? DEFAULT_ACE_THEME)
  const modeAbilities = getModeAbilities(props.mode)
  const isExperimentalUx = useExperimentalConfiguratorUx()
  const onRunRef = useLatest(props.onRun) // ace binds commands once on mount

  // prevent also scrolling parent element
  // workaround as it doesn't support overscroll-behavior-y: contain
  useEffect(() => {
    if (!editorRef.current) return
    editorRef.current.editor.container.addEventListener("wheel", e => {
      if (!focusedRef.current) return
      e.preventDefault() // prevent scrolling parent if focused
    })
  }, [])

  useEffect(() => {
    if (!isFloating && !props.autoFocus) return
    editorRef.current?.editor.focus()
  }, [isFloating, props.autoFocus])

  useSyncCodeEditor({
    enabled: !props.floating,
    mode: props.mode,
    name: props.name,
    value: props.value,
    editorRef,
    focusedRef,
  })

  const checkValue = () => {
    if (!modeAbilities.parse) return
    try {
      modeAbilities.parse(props.value)
      setValidationStatus("ok")
    } catch (e) {
      setValidationStatus(String(e))
    }
  }

  const formatValue = () => {
    if (!modeAbilities.format) return
    try {
      modeAbilities.format(props.value, formatted => props.onValueChange?.(formatted))
    } catch (e) {
      // invalid input cannot be formatted, the check button reports why
      console.error(`could not format ${modeAbilities.label}`, e)
    }
  }

  const downloadValue = () => {
    const extension = props.extension || modeAbilities.extension
    const blob = new Blob([props.value], { type: modeAbilities.mimeType })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = props.exportFileName || `${props.name || 'data'}.${extension}`
    a.click()
    URL.revokeObjectURL(url)
  }

  const importValueFromFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const text = e.target?.result
        if (text) {
          props.onValueChange?.(text as string)
        }
      }
      reader.readAsText(file)
    }
  }

  const copyValueToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(props.value)
    } catch (err) {
      console.error(err)
      alert('Failed to copy')
    }
  }

  const pasteValueFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText()
      props.onValueChange?.(text)
    } catch (err) {
      console.error(err)
      alert('Failed to paste')
    }
  }

  const floatingId = "code-editor-" + props.name
  const useYasqeWhenExpanded = localSettings.sparqlEditor === "yasqe" && props.mode === "sparql"

  return (
    <div className={mergeClasses(`
          group position-relative flex-grow
          flex flex-col overflow-hidden
          border border-border rounded-md`)}>
      <FontLoader />
      {(!isFloating && isExperimentalUx) && (
        <div className="
        position-absolute z-10
        bg-white rounded ring-2 !ring-white
        opacity-0 group-hover:opacity-100
        pointer-events-none group-hover:pointer-events-auto
        transition duration-300 ease-in
      " style={{ top: '2px', right: '2px' }}>
          <MiniButton title="Open floating editor" onClick={() => {
            setSpawnFloating(true)
            usePanelRegistry.getState().floatPanel(floatingId)
          }}>
            <OpenInNewWindowIcon width="14" height="14" />
          </MiniButton>
        </div>
      )}
      <style>{`
       .ace_scroller.ace_scroll-left:after {
         box-shadow: initial !important; /* otherwise ugly */
       }
       .ace_scrollbar {
         scrollbar-width: thin; /* maybe auto if not docked? */
       }
      `}</style>
      <AceEditor
        ref={editorRef}
        name={props.name}
        mode={props.mode}
        theme={loadedAceTheme?.name ?? ""}
        fontSize={
          isFloating
            ? (localSettings.fontSize || "14px")
            : (localSettings.fontSizeDocked || "12px")
        }
        style={{
          width: '100%',
          height: (isFloating || props.fullHeight) ? "100%" : "auto",
          minHeight: (isFloating || props.fullHeight) ? "100%" : (props.minHeight || localSettings.editorHeightDocked || "120px"),
          fontFamily: `"${localSettings.fontFamily}", Monaco, Consolas, "Lucida Console", monospace`,
          fontWeight: localSettings.fontWeight || "400",
          resize: isFloating ? "none" : "vertical"
        }}
        value={props.value}
        readOnly={props.readonly}
        showGutter={props.showGutter}
        enableBasicAutocompletion={props.mode === "turtle"}
        enableLiveAutocompletion={props.mode === "turtle"}
        commands={props.onRun ? [{
          name: 'run',
          bindKey: { win: 'Ctrl-Enter', mac: 'Command-Enter' },
          exec: () => onRunRef.current?.(),
        }] : undefined}
        onFocus={() => focusedRef.current = true}
        onBlur={(_event, editor) => {
          if (!editor) {
            throw new Error("huh, no editor")
          }
          const value = editor.getSession().getValue()
          focusedRef.current = false
          if (!props.publishValueChangeOnBlur) return
          props.onValueChange?.(value)
        }}
        onChange={value => {
          if (props.readonly) return
          setValidationStatus(null)
          if (props.publishValueChangeOnBlur) return
          props.onValueChange?.(value)
        }}
      />
      <div className="flex gap-1 px-1 bg-muted/40 border-t border-border text-muted-foreground">
        {(modeAbilities.parse && !props.readonly) && (
          <MiniButton title="Check" onClick={() => checkValue()}>
            check
          </MiniButton>
        )}
        {validationStatus && (
          <div className={mergeClasses('flex min-w-0 gap-1 items-center whitespace-nowrap', {
            'text-red-600': validationStatus !== 'ok',
            'text-green-600': validationStatus === 'ok',
          })}>
            <div style={{ width: '14px' }}>
              {validationStatus === 'ok' ? <Check size={14} /> : <TriangleAlert size={14} />}
            </div>
            <span className="truncate" title={validationStatus}>
              {validationStatus === 'ok'
                ? `Valid ${modeAbilities.label}`
                : String(validationStatus)}
            </span>
          </div>
        )}
        <div className="flex-grow" />
        {(modeAbilities.format && !props.readonly) && (
          <MiniButton title={`Format ${modeAbilities.label}`} onClick={() => formatValue()}>
            <Braces size={14} />
          </MiniButton>
        )}
        {(props.localHistoryKey && !props.readonly) && (
          <HistoryMenu mode={props.mode}
                       localHistoryKey={props.localHistoryKey}
                       value={props.value}
                       onRestore={value => props.onValueChange?.(value)} />
        )}
        <MiniButton title="Download" onClick={() => downloadValue()}>
          <Download size={14} />
        </MiniButton>
        {!props.readonly && (
          <MiniButton title="Upload" onClick={() => inputRef.current?.click?.()}>
            <input
              ref={inputRef}
              type="file"
              accept={modeAbilities.fileAccept}
              style={{ display: "none" }}
              onChange={importValueFromFile}
            />
            <Upload size={14} />
          </MiniButton>
        )}
        <MiniButton title="Copy to clipboard" onClick={() => copyValueToClipboard()}>
          <Copy size={14} />
        </MiniButton>
        {!props.readonly && (
          <MiniButton title="Paste from clipboard" onClick={() => pasteValueFromClipboard()}>
            <Clipboard size={14} />
          </MiniButton>
        )}
      </div>
      {spawnFloating && (
        <LocalEditorState
          value={props.value}
          onChange={props.onValueChange ?? (() => {})}>
          {(value, onChange, publish, isDirty) => (
            <Panel id={floatingId}
                   role="floating-only"
                   title={`${props.name} - ${props.mode.toUpperCase()}`}
                   onClose={() => setSpawnFloating(false)}
                   defaultVisible
                   defaultFloatingDimensions={{
                     width: 800,
                     height: 600
                   }}
                   showCloseButton={false}
                   renderHeaderExtraIcons={actions => (
                     <>
                       <TopBarButton title="Discard changes & close"
                                     variant="outline"
                                     className="h-full rounded-md font-exo"
                                     onClick={() => {
                                       if (!isDirty || window.confirm("There are unapplied changes. Are you sure?")) {
                                         actions.close()
                                       }
                                     }}>
                         Cancel
                       </TopBarButton>
                       <TopBarSeparator />
                       <TopBarButton title="Apply changes"
                                     variant="outline"
                                     className="h-full rounded-md font-exo"
                                     onClick={publish}>
                         Apply
                       </TopBarButton>
                       <TopBarButton title="Apply changes & close"
                                     variant="purple"
                                     className="h-full rounded-md font-exo"
                                     onClick={() => {
                                       publish()
                                       actions.close()
                                     }}>
                         Done
                       </TopBarButton>
                     </>
                   )}
            >
              {useYasqeWhenExpanded ? (
                <SparqlEditor
                  mode="init-value"
                  value={value}
                  onBlur={onChange}
                  id={`code-editor-expanded-${props.name}`} />
              ) : (
                <CodeEditor name={props.name}
                            mode={props.mode}
                            floating
                            value={value}
                            onValueChange={onChange} />
              )}
            </Panel>
          )}
        </LocalEditorState>
      )}
    </div>
  );
}

function LocalEditorState(props: {
  value: string
  onChange: (value: string) => void
  children: (
    value: string,
    onChange: (value: string) => void,
    publish: () => void,
    isDirty: boolean
  ) => React.ReactNode
}) {
  const [ value, setValue ] = useState(props.value)
  const [ isDirty, setIsDirty ] = useState(false)
  const latestValueRef = useRef(value)
  latestValueRef.current = value

  const publish = useCallback(() => {
    props.onChange(latestValueRef.current)
  }, [ props.onChange ])

  const onChange = useCallback((newValue: string) => {
    if (newValue === latestValueRef.current) return
    setValue(newValue)
    setIsDirty(true)
  }, [ setValue, setIsDirty ])

  return props.children(value, onChange, publish, isDirty)
}

/** Load only required monospace fonts dynamically */
function FontLoader() {
  const fontFamily = useLocalSettings(st => st.fontFamily)

  useEffect(() => {
    async function load() {
      if (fontFamily === "Inconsolata Variable") {
        // @ts-expect-error it works but TS doesn't like it
        await import("@fontsource-variable/inconsolata")
        await import("@fontsource-variable/inconsolata/wght.css")
      }
      if (fontFamily === "Cascadia Code") {
        // @ts-expect-error it works but TS doesn't like it
        await import("@fontsource/cascadia-code")
        await import("@fontsource/cascadia-code/400.css")
        await import("@fontsource/cascadia-code/500.css")
      }
    }
    void load()
  }, [ fontFamily ])

  return null
}

function useLoadedAceTheme(aceTheme: AceThemeName): LoadedAceTheme | undefined {
  const [ loaded, setLoaded ] = useState(() => getLoadedAceTheme(aceTheme))

  useEffect(function loadTheme() {
    const alreadyLoaded = getLoadedAceTheme(aceTheme)
    if (alreadyLoaded) return setLoaded(alreadyLoaded)

    setLoaded(undefined)
    let outdated = false
    loadAceTheme(aceTheme)
      .then(theme => { if (!outdated) setLoaded(theme) })
      .catch(e => console.error(`could not load ace theme "${aceTheme}"`, e))

    return () => { outdated = true }
  }, [ aceTheme ])

  return loaded
}


function useSyncCodeEditor(params: {
  enabled: boolean
  mode: CodeEditorMode
  name: string | undefined
  value: string
  editorRef: React.RefObject<ReactAce>
  focusedRef: React.MutableRefObject<boolean>
}) {
  const isEnabled = params.enabled && Boolean(params.name)

  const valueRef = useLatest(params.value)
  const settledListenersRef = useRef(new Set<() => void>())

  useEffect(function notifyListenersOnExternallyAppliedValue() {
    if (params.focusedRef.current) return
    settledListenersRef.current.forEach(notify => notify())
  }, [params.value, params.focusedRef])

  useEffect(function publishEditorToRegistry() {
    const editor = params.editorRef.current?.editor
    if (!isEnabled || !params.name || !editor) return
    const id = `code-editor-${params.name}`
    const handle: CodeEditorHandle = {
      mode: params.mode,
      getValue: () => valueRef.current,
      onSettled: handler => {
        const listeners = settledListenersRef.current
        listeners.add(handler)
        editor.on('blur', handler)
        return () => {
          listeners.delete(handler)
          editor.off('blur', handler)
        }
      },
    }
    store.dispatch({ type: 'ADD_CODE_EDITOR', data: { id, editor: handle } })
    return () => {
      store.dispatch({ type: 'REMOVE_CODE_EDITOR', data: id })
    }
  }, [isEnabled, params.mode, params.name, params.editorRef, valueRef])
}
